import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshLambertMaterial,
  type BufferGeometry,
  type Material,
} from 'three';

/**
 * Power-ups of GDD §7 in low-poly primitives with flat shading (issue #82): the pickups in the scene and
 * the states of La Chiva. The models never change a hitbox (the 0.6 u circle of the pickups, the 1.2 x
 * 0.5 u box of the plane). Geometries and materials are created once per scene and shared by every copy.
 */
export type PowerUpPickup = 'armor' | 'bribe' | 'scapular';

/** Disco colours of the bribe, cycled at 4 Hz; reduced motion keeps them fixed (GDD §13.6, no flashes). */
export const DISCO_COLORS = [0xd94bd1, 0x4bd9d0, 0xe8d24a, 0x6bd94b] as const;
const DISCO_HZ = 4;

export interface PowerUpModels {
  /** A new pickup model of the given type, named pickup-<type>. */
  pickup(type: PowerUpPickup): Group;
  /** Plates bolted to the airframe while armored, child of the player group. */
  readonly armorPlates: Group;
  /** Lights around the fuselage while the bribe is active. */
  readonly discoLights: Group;
  /** Turns the disco colours for the render time in seconds; fixed when `steady`. */
  updateDisco(seconds: number, steady: boolean): void;
  dispose(): void;
}

export function buildPowerUpModels(): PowerUpModels {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const own = <T extends BufferGeometry>(geometry: T): T => {
    geometries.push(geometry);
    return geometry;
  };
  const paint = (color: number, emissive = 0x000000) => {
    const material = new MeshLambertMaterial({ color, emissive, flatShading: true });
    materials.push(material);
    return material;
  };

  const steel = paint(0x9aa3ad);
  const bolt = paint(0x4a4f57);
  const leather = paint(0x6b4a2b);
  const bill = paint(0x7fa36a);
  const cloth = paint(0x7a5230);
  const glow = paint(0xe8c46a, 0x8a6a20);

  const plate = own(new BoxGeometry(0.5, 0.42, 0.06));
  const boltGeometry = own(new CylinderGeometry(0.035, 0.035, 0.04, 5));
  const caseGeometry = own(new BoxGeometry(0.6, 0.36, 0.2));
  const lidGeometry = own(new BoxGeometry(0.6, 0.04, 0.36));
  const billGeometry = own(new BoxGeometry(0.42, 0.04, 0.24));
  const scapularGeometry = own(new BoxGeometry(0.28, 0.34, 0.04));
  const cordGeometry = own(new CylinderGeometry(0.012, 0.012, 0.5, 4));
  const lightGeometry = own(new IcosahedronGeometry(0.07, 0));
  const armorPlate = own(new BoxGeometry(0.55, 0.06, 0.42));

  const builders: Record<PowerUpPickup, () => Group> = {
    // A metal plate with its four bolts.
    armor: () => {
      const group = new Group();
      group.add(new Mesh(plate, steel));
      for (const [x, y] of [[-0.18, -0.15], [0.18, -0.15], [-0.18, 0.15], [0.18, 0.15]] as const) {
        const head = new Mesh(boltGeometry, bolt);
        head.rotation.x = Math.PI / 2;
        head.position.set(x, y, 0.04);
        group.add(head);
      }
      return group;
    },
    // An open briefcase with bills.
    bribe: () => {
      const group = new Group();
      group.add(new Mesh(caseGeometry, leather));
      const lid = new Mesh(lidGeometry, leather);
      lid.position.set(0, 0.32, -0.1);
      lid.rotation.x = -1.1;
      const bills = new Mesh(billGeometry, bill);
      bills.position.y = 0.2;
      group.add(lid, bills);
      return group;
    },
    // A hanging scapular that glows.
    scapular: () => {
      const group = new Group();
      const cord = new Mesh(cordGeometry, cloth);
      cord.position.y = 0.3;
      group.add(cord, new Mesh(scapularGeometry, glow));
      return group;
    },
  };

  const armorPlates = new Group();
  armorPlates.name = 'player-armor-plates';
  for (const y of [0.24, -0.24]) {
    const mesh = new Mesh(armorPlate, steel);
    mesh.position.set(0.05, y, 0);
    armorPlates.add(mesh);
  }

  const discoLights = new Group();
  discoLights.name = 'player-disco-lights';
  const lightMaterials = DISCO_COLORS.map((color) => paint(color, color));
  lightMaterials.forEach((material, index) => {
    const light = new Mesh(lightGeometry, material);
    const angle = (index / lightMaterials.length) * Math.PI * 2;
    light.position.set(Math.cos(angle) * 0.7, Math.sin(angle) * 0.4, 0.3);
    discoLights.add(light);
  });

  return {
    pickup(type) {
      const group = builders[type]();
      group.name = `pickup-${type}`;
      return group;
    },
    armorPlates,
    discoLights,
    updateDisco(seconds, steady) {
      const shift = steady ? 0 : Math.floor(seconds * DISCO_HZ);
      lightMaterials.forEach((material, index) => {
        const color = DISCO_COLORS[(index + shift) % DISCO_COLORS.length]!;
        material.color.setHex(color);
        material.emissive.setHex(color);
      });
    },
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
